/**
 * Svelte action — pull-down "L-gesture" façon Superhuman mobile.
 *
 *   ◯ Pull down (vertical, dx ≈ 0)              → reveal SEARCH
 *   ◯ Pull down PUIS swipe left ("L" gesture)   → REFRESH
 *
 * Algo :
 *  - touchstart valide UNIQUEMENT si le scroll de la page est à 0
 *  - touchmove : suit le doigt en translateY (résistance 0.5)
 *  - mode actif (search vs refresh) calculé en continu :
 *      • dy >= ARM_DY  → "search" (état armé)
 *      • dy >= ARM_DY ET dx <= -SWITCH_DX → "refresh" (latched, ne revient pas)
 *  - touchend :
 *      mode = refresh  → onRefresh()
 *      mode = search   → onReveal()
 *      sinon           → snap-back
 *
 * Indicateur visuel : si `indicator` est passé, on lui pose un attribut
 * `data-pull-mode="search"|"refresh"` que le CSS du caller utilise pour
 * swap icône + label pendant le drag (cf. PullIndicator.svelte).
 */

import { haptic } from "./haptic";

export interface PullDownOptions {
  onRefresh: () => void | Promise<void>;
  onReveal: () => void;
  enabled?: boolean;
  /**
   * Élément qui reçoit l'attribut `data-pull-mode="search"|"refresh"|null`
   * pendant le geste — permet au CSS de switcher l'icône/label visible.
   */
  indicator?: HTMLElement | null;
}

const RESISTANCE = 0.5;
/** dy minimum (post-résistance) pour considérer le geste "armé" sur search. */
const ARM_DY = 50;
/** Si pulled past ARM_DY puis swipe left ≥ SWITCH_DX → bascule sur refresh. */
const SWITCH_DX = 50;
const MAX_PULL = 180;
const SNAP_BACK_MS = 220;
const EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

type PullMode = "search" | "refresh" | null;

export function pullDown(node: HTMLElement, options: PullDownOptions) {
  let opts = options;
  let startX = 0;
  let startY = 0;
  let active = false;
  let aborted = false;
  let currentDy = 0;
  let mode: PullMode = null;

  function setTransform(y: number) {
    node.style.transform = y === 0 ? "" : `translateY(${y}px)`;
  }

  function setMode(m: PullMode) {
    if (m === mode) return;
    const prev = mode;
    mode = m;
    if (opts.indicator) {
      if (m) opts.indicator.setAttribute("data-pull-mode", m);
      else opts.indicator.removeAttribute("data-pull-mode");
    }
    // Haptic léger au franchissement du seuil → l'user sent qu'il vient
    // de "verrouiller" la search ou la refresh.
    if (m === "search" && prev === null) haptic("light");
    if (m === "refresh" && prev === "search") haptic("light");
  }

  function animateBack() {
    node.style.transition = `transform ${SNAP_BACK_MS}ms ${EASING}`;
    setTransform(0);
    window.setTimeout(() => {
      node.style.transition = "";
    }, SNAP_BACK_MS);
  }

  function isAtTop(): boolean {
    return window.scrollY <= 0;
  }

  function onTouchStart(e: TouchEvent) {
    if (opts.enabled === false) return;
    if (e.touches.length !== 1) return;
    if (!isAtTop()) return;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    active = true;
    aborted = false;
    currentDy = 0;
    setMode(null);
    node.style.transition = "";
  }

  function onTouchMove(e: TouchEvent) {
    if (!active || aborted) return;
    const t = e.touches[0];
    const dy = t.clientY - startY;
    const dx = t.clientX - startX;

    if (dy <= 0) {
      currentDy = 0;
      setTransform(0);
      setMode(null);
      return;
    }
    if (!isAtTop() && currentDy === 0) {
      aborted = true;
      return;
    }

    currentDy = Math.min(MAX_PULL, dy * RESISTANCE);
    setTransform(currentDy);

    // Mode :
    //   < ARM_DY                 → null (snap-back si release ici)
    //   ≥ ARM_DY                 → "search"
    //   ≥ ARM_DY + dx ≤ -SWITCH_DX → "refresh" (latched : ne revient PAS sur "search"
    //                              même si l'user remonte un peu vers la droite)
    if (currentDy >= ARM_DY) {
      if (mode !== "refresh" && dx <= -SWITCH_DX) {
        setMode("refresh");
      } else if (mode === null) {
        setMode("search");
      }
    } else {
      setMode(null);
    }

    if (e.cancelable && (currentDy > 5 || Math.abs(dx) > 10)) e.preventDefault();
  }

  function onTouchEnd() {
    if (!active) return;
    active = false;
    const finalMode = mode;
    setMode(null);

    if (aborted || currentDy === 0) {
      animateBack();
      return;
    }

    if (finalMode === "refresh") {
      haptic("medium");
      animateBack();
      void opts.onRefresh();
    } else if (finalMode === "search") {
      animateBack();
      opts.onReveal();
    } else {
      animateBack();
    }
  }

  node.addEventListener("touchstart", onTouchStart, { passive: true });
  node.addEventListener("touchmove", onTouchMove, { passive: false });
  node.addEventListener("touchend", onTouchEnd, { passive: true });
  node.addEventListener("touchcancel", onTouchEnd, { passive: true });

  return {
    update(newOpts: PullDownOptions) {
      opts = newOpts;
    },
    destroy() {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    },
  };
}
